import axios from "axios";
import { supabase } from "./supabaseClient";

// Obter API URL do sistema de configuração dinâmica
const getApiUrl = () => {
  return (
    window.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

const axiosInstance = axios.create({
  baseURL: API_URL,
});

export const clientService = {
  async createClient(clientData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para criar clientes."
        );
      }

      const response = await axiosInstance.post(`/clients`, clientData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao criar cliente"
        );
      }
      throw error;
    }
  },

  async getClients(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar clientes."
        );
      }

      const response = await axiosInstance.get(`/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar clientes"
        );
      }
      throw error;
    }
  },

  async searchClients(term) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar clientes."
        );
      }

      const response = await axiosInstance.get(`/clients/search`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { term },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar clientes"
        );
      }
      throw error;
    }
  },

  async getClientById(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar detalhes do cliente."
        );
      }

      const response = await axiosInstance.get(`/clients/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar detalhes do cliente"
        );
      }
      throw error;
    }
  },

  async updateClient(id, clientData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar clientes."
        );
      }

      const response = await axiosInstance.put(`/clients/${id}`, clientData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar cliente"
        );
      }
      throw error;
    }
  },

  async deleteClient(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para deletar clientes."
        );
      }

      const response = await axiosInstance.delete(`/clients/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao deletar cliente"
        );
      }
      throw error;
    }
  },

  // Gestão de produtos desativados por cliente
  async getClientDisabledProducts(clientId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.get(`/clients/${clientId}/disabled-products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos desativados"
        );
      }
      throw error;
    }
  },

  async addDisabledProducts(clientId, productIds) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.post(
        `/clients/${clientId}/disabled-products`,
        { product_ids: productIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao desativar produtos"
        );
      }
      throw error;
    }
  },

  async removeDisabledProduct(clientId, productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.delete(
        `/clients/${clientId}/disabled-products/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao reativar produto"
        );
      }
      throw error;
    }
  },

  // ===== GESTÃO DE PRODUTOS ESPECÍFICOS POR CLIENTE =====
  // Produtos com for_all_clients=FALSE que precisam ser ativados individualmente

  // Buscar produtos específicos disponíveis (for_all_clients=FALSE) e seus status para o cliente
  async getAvailableSpecificProducts(clientId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.get(`/clients/${clientId}/available-specific-products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos específicos"
        );
      }
      throw error;
    }
  },

  // Ativar produtos específicos para o cliente
  async addSpecificProducts(clientId, productIds) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.post(
        `/clients/${clientId}/specific-products`,
        { product_ids: productIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao ativar produtos específicos"
        );
      }
      throw error;
    }
  },

  // Desativar produto específico para o cliente
  async removeSpecificProduct(clientId, productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await axiosInstance.delete(
        `/clients/${clientId}/specific-products/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao desativar produto específico"
        );
      }
      throw error;
    }
  },
};